import { Flex, Label, Link, Text } from '@raystack/apsara';

import { CONSTELLATION_IDS_CONSTS } from '@src/utils/constants/LENS_CONSTANTS';

export const SatelliteInfo = [
  {
    id: CONSTELLATION_IDS_CONSTS.LANDSAT_8_9,
    name: 'Landsat-8 and 9',
    description: (
      <>
        <Flex direction='column' gap='large' style={{ width: '400px' }}>
          <Text size={2}>
            Landsat 8 and 9 are part of the Landsat mission by NASA and the
            USGS. Both the satellites carry the Operational Land Imager (OLI)
            and the Thermal Infrared Sensor (TIRS) instruments, which capture 8
            bands at a combined revisit frequency of 8 days.
          </Text>

          <Text size={2}>
            <Flex direction='column' gap='small'>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Data Availability</span>
                <span>:</span>
                <span>From March 2013 to Present</span>
              </Flex>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Resolution</span>
                <span>:</span>
                <span>30 meters*</span>
              </Flex>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Sensor Type</span>
                <span>:</span>
                <span>Multispectral</span>
              </Flex>
            </Flex>
            <Label size='small' style={{ marginTop: '16px' }}>
              * The Surface Temperature Band (lwir11) has been resampled to 30
              meters resolution for consistency using the&nbsp;
              <Link
                variant='accent'
                href='https://gdal.org/programs/gdalwarp.html#cmdoption-gdalwarp-r'
                external
                data-test-id='official-documentation-link'
              >
                lanczos resampling algorithm.
              </Link>
              &nbsp;
            </Label>
          </Text>

          <Text size={2}>
            Note: Aurora offers the Science Product (SP) Dataset, which includes
            atmospherically corrected Surface Reflectance (SR) and Surface
            Temperature (ST) bands from Landsat imagery.
          </Text>

          <Text size={2}>
            For more details, refer to the{' '}
            <Link
              variant='accent'
              href='https://www.usgs.gov/landsat-missions/landsat-collection-2-level-2-science-products'
              external
              data-test-id='official-documentation-link'
            >
              official documentation
            </Link>
            .
          </Text>
        </Flex>
      </>
    ),
    show: true
  },
  {
    id: CONSTELLATION_IDS_CONSTS.SENTINEL_2,
    name: 'Sentinel-2',
    description: (
      <>
        <Flex direction='column' gap='large' style={{ width: '400px' }}>
          <Text size={2}>
            Sentinel-2 is an Earth observation mission from the Copernicus
            Programme. The data is available from the two satellites of the
            constellation, Sentinel-2A and Sentinel-2B, which have 13 bands with
            a revisit frequency of 5 days at the Equator.
          </Text>

          <Text size={2}>
            <Flex direction='column' gap='small'>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Data Availability</span>
                <span>:</span>
                <span>From January 2017 to Present</span>
              </Flex>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Resolution</span>
                <span>:</span>
                <span>10 meters*</span>
              </Flex>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Sensor Type</span>
                <span>:</span>
                <span>Multispectral</span>
              </Flex>
            </Flex>
            <Label size='small' style={{ marginTop: '16px' }}>
              * All the bands are resampled to 10 meters resolution for
              consistency using&nbsp;
              <Link
                variant='accent'
                href='https://gdal.org/programs/gdalwarp.html#cmdoption-gdalwarp-r'
                external
                data-test-id='official-documentation-link'
              >
                lanczos resampling algorithm.
              </Link>
              &nbsp;
            </Label>
          </Text>

          <Text size={2}>
            For more details, refer to the{' '}
            <Link
              variant='accent'
              href='https://sentiwiki.copernicus.eu/web/sentinel-2'
              external
              data-test-id='official-documentation-link'
            >
              official documentation
            </Link>
            .
          </Text>
        </Flex>
      </>
    ),
    show: true
  },
  {
    id: CONSTELLATION_IDS_CONSTS.MODIS_NBAR_DAILY,
    name: 'Modis NBAR Daily',
    description: (
      <>
        <Flex direction='column' gap='large' style={{ width: '400px' }}>
          <Text size={2}>
            The Moderate Resolution Imaging Spectroradiometer (MODIS) Nadir
            Bidirectional Reflectance Distribution Function (BRDF)-Adjusted
            Reflectance (NBAR) dataset provides daily NBAR and simplified
            mandatory quality layers for MODIS bands 1 through 7, using 16 days
            of Terra and Aqua MODIS data at a resolution of 500 meters.
          </Text>

          <Text size={2}>
            <Flex direction='column' gap='small'>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Data Availability</span>
                <span>:</span>
                <span>From February 2000 to Present</span>
              </Flex>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Resolution</span>
                <span>:</span>
                <span>500 meters</span>
              </Flex>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Sensor Type</span>
                <span>:</span>
                <span>Multispectral</span>
              </Flex>
            </Flex>
          </Text>

          <Text size={2}>
            For more details, refer to the{' '}
            <Link
              variant='accent'
              href='https://lpdaac.usgs.gov/products/mcd43a4v006/'
              external
              data-test-id='official-documentation-link'
            >
              official documentation
            </Link>
            .
          </Text>
        </Flex>
      </>
    ),
    show: true
  },
  {
    id: CONSTELLATION_IDS_CONSTS.PIXXEL_D2,
    name: 'Pixxel-D2',
    description: (
      <>
        <Flex direction='column' gap='large' style={{ width: '400px' }}>
          <Text size={2}>
            Technology Demonstration Satellite-1, also known as D2, was launched
            in June 2021 and decommissioned in August 2024. It was equipped with
            an imager capable of capturing 159 bands between 471.5 and 896 nm.
          </Text>

          <Text size={2}>
            <Flex direction='column' gap='small'>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Data Availability</span>
                <span>:</span>
                <span>From August 2022 - August 2024</span>
              </Flex>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Resolution</span>
                <span>:</span>
                <span>30 meters</span>
              </Flex>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Sensor Type</span>
                <span>:</span>
                <span>Hyperspectral</span>
              </Flex>
            </Flex>
          </Text>

          <Text size={2}>
            Note: Aurora archives provide only the L2A Level data for specific
            geographies. Please contact our customer success team at{' '}
            <Link
              variant='accent'
              href='mailto:support@pixxel.space'
              external
              data-test-id='official-documentation-link'
            >
              support@pixxel.space
            </Link>{' '}
            to order images for L1C Level data.
          </Text>

          <Text size={2}>
            For more details, refer to the{' '}
            <Link
              variant='accent'
              href='https://support.pixxel.space/hc/en-us/articles/9588039261724-Pixxel-Product-Specifications-Guide'
              external
              data-test-id='official-documentation-link'
            >
              product documentation
            </Link>
            .
          </Text>
        </Flex>
      </>
    ),
    show: true
  },
  {
    id: CONSTELLATION_IDS_CONSTS.SHAKUNTALA,
    name: 'Shakuntala',
    description: (
      <>
        <Flex direction='column' gap='large' style={{ width: '400px' }}>
          <Text size={2}>
            Pixxel Technology Demonstration (TD) Satellite-2, also known as
            Shakuntala, was launched in April 2022 and decommissioned in January
            2024. The imager was capable of capturing 151 bands between 470 and
            894 nm.
          </Text>

          <Text size={2}>
            <Flex direction='column' gap='small'>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '125px' }}>Data Availability</span>
                <span>:</span>
                <span>From November 2022 to December 2023</span>
              </Flex>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Resolution</span>
                <span>:</span>
                <span>10 meters</span>
              </Flex>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Sensor Type</span>
                <span>:</span>
                <span>Hyperspectral</span>
              </Flex>
            </Flex>
          </Text>

          <Text size={2}>
            Aurora archives provide only the L2A Level data for specific
            geographies. Please contact our customer success team at{' '}
            <Link
              variant='accent'
              href='mailto:support@pixxel.space'
              external
              data-test-id='official-documentation-link'
            >
              support@pixxel.space
            </Link>{' '}
            to order images for L1C Level data,
          </Text>

          <Text size={2}>
            For more details, refer to the{' '}
            <Link
              variant='accent'
              href='https://support.pixxel.space/hc/en-us/articles/9588039261724-Pixxel-Product-Specifications-Guide'
              external
              data-test-id='official-documentation-link'
            >
              product documentation
            </Link>
            .
          </Text>
        </Flex>
      </>
    ),
    show: true
  },
  {
    id: CONSTELLATION_IDS_CONSTS.SENTINEL_1_RTC,
    name: 'Sentinel-1 (RTC)',
    description: (
      <>
        <Flex direction='column' gap='large' style={{ width: '400px' }}>
          <Text size={2}>
            Sentinel-1 is a Radar vision mission by Copernicus providing
            all-weather, day-and-night C-band radar imagery for the global
            monitoring of Earth's land and oceans. This data is available in
            adiometrically terrain corrected product from the two satellites
            constellation, Sentinel-1A and Sentinel-1B (retired in 2022).
          </Text>

          <Text size={2}>
            <Flex direction='column' gap='small'>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Data Availability</span>
                <span>:</span>
                <span>From October 2014 to Present</span>
              </Flex>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Resolution</span>
                <span>:</span>
                <span>10 meters</span>
              </Flex>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Sensor Type</span>
                <span>:</span>
                <span>Synthetic Aperture Radar (SAR)</span>
              </Flex>
            </Flex>
          </Text>

          <Text size={2}>
            For more details, refer to the{' '}
            <Link
              variant='accent'
              href='https://planetarycomputer.microsoft.com/dataset/sentinel-1-rtc'
              external
              data-test-id='official-documentation-link'
            >
              source documentation
            </Link>
            .
          </Text>
        </Flex>
      </>
    ),
    show: true
  },
  {
    id: CONSTELLATION_IDS_CONSTS.MODIS_LSTE_8_DAY,
    name: 'MODIS LSTE 8-Day',
    description: (
      <>
        <Flex direction='column' gap='large' style={{ width: '400px' }}>
          <Text size={2}>
            The Moderate Resolution Imaging Spectroradiometer (MODIS) Land
            Surface Temperature/Emissivity 8-Day product consists of 2 bands
            that provide an average 8-day per-pixel Land Surface Temperature and
            Emissivity (LST&E) for the captured area.
          </Text>

          <Text size={2}>
            <Flex direction='column' gap='small'>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Data Availability</span>
                <span>:</span>
                <span>From February 2000 to Present</span>
              </Flex>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Resolution</span>
                <span>:</span>
                <span>1000 meters</span>
              </Flex>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Sensor Type</span>
                <span>:</span>
                <span>Multispectral</span>
              </Flex>
            </Flex>
          </Text>

          <Text size={2}>
            Note: The date shown on the Aurora for this dataset refers to the
            start date of the 8-day acquisition period of the tile.
          </Text>

          <Text size={2}>
            For more details, refer to the{' '}
            <Link
              variant='accent'
              href='https://modis.gsfc.nasa.gov/data/dataprod/mod11.php'
              external
              data-test-id='official-documentation-link'
            >
              official documentation
            </Link>
            .
          </Text>
        </Flex>
      </>
    ),
    show: true
  },
  {
    id: CONSTELLATION_IDS_CONSTS.MODIS_SURFACE_REFLECTANCE_8_DAY,
    name: 'MODIS Surface Reflectance 8-Day',
    description: (
      <>
        <Flex direction='column' gap='large' style={{ width: '400px' }}>
          <Text size={2}>
            The Moderate Resolution Imaging Spectroradiometer (MODIS) Surface
            Reflectance product consists of 2 bands that provide the best
            possible surface reflectance observation during a period of 8-days.
          </Text>

          <Text size={2}>
            <Flex direction='column' gap='small'>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Data Availability</span>
                <span>:</span>
                <span>From February 2000 to Present</span>
              </Flex>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Resolution</span>
                <span>:</span>
                <span>250 meters</span>
              </Flex>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Sensor Type</span>
                <span>:</span>
                <span>Multispectral</span>
              </Flex>
            </Flex>
          </Text>

          <Text size={2}>
            Note: The date shown on the Aurora for this dataset refers to the
            start date of the 8-day acquisition period of the tile.
          </Text>

          <Text size={2}>
            For more details, refer to the{' '}
            <Link
              variant='accent'
              href='https://modis.gsfc.nasa.gov/data/dataprod/mod09.php'
              external
              data-test-id='official-documentation-link'
            >
              official documentation
            </Link>
            .
          </Text>
        </Flex>
      </>
    ),
    show: true
  },
  {
    id: CONSTELLATION_IDS_CONSTS.EO_01_HYPERION,
    name: 'EO-01 Hyperion',
    description: (
      <>
        <Flex direction='column' gap='large' style={{ width: '400px' }}>
          <Text size={2}>
            Earth Observing One (EO-1) - Hyperion was launched on November 21,
            2000 by NASA and USGS. Hyperion Instrument captures 220 unique
            spectral bands between 357 to 2576 nm.
          </Text>

          <Text size={2}>
            <Flex direction='column' gap='small'>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Data Availability</span>
                <span>:</span>
                <span>From June 2001 to March 2017</span>
              </Flex>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Resolution</span>
                <span>:</span>
                <span>30 meters</span>
              </Flex>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Sensor Type</span>
                <span>:</span>
                <span>Hyperspectral</span>
              </Flex>
            </Flex>
          </Text>

          <Text size={2}>
            For more details, refer to the{' '}
            <Link
              variant='accent'
              href='https://www.usgs.gov/centers/eros/science/usgs-eros-archive-earth-observing-one-eo-1-hyperion'
              external
              data-test-id='official-documentation-link'
            >
              official documentation
            </Link>
            .
          </Text>
        </Flex>
      </>
    ),
    show: true
  },
  {
    id: CONSTELLATION_IDS_CONSTS.PIXXEL_FIREFLY,
    name: 'Pixxel-Firefly',
    description: (
      <>
        <Flex direction='column' gap='large' style={{ width: '400px' }}>
          <Text size={2}>
            Pixxel-Firefly Constellation, was launched in January 2025. It is
            equipped with an imager capable of capturing 145 bands between 470
            and 900 nm.
          </Text>

          <Text size={2}>
            <Flex direction='column' gap='small'>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Data Availability</span>
                <span>:</span>
                <span>From June 2025 - Present</span>
              </Flex>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Resolution</span>
                <span>:</span>
                <span>5.4 meters</span>
              </Flex>
              <Flex direction='row' gap='extra-large'>
                <span style={{ width: '100px' }}>Sensor Type</span>
                <span>:</span>
                <span>Hyperspectral</span>
              </Flex>
            </Flex>
          </Text>

          <Text size={2}>
            Aurora archives provide only the L2A Level data for specific
            geographies. Please contact our customer success team at{' '}
            <Link
              variant='accent'
              href='mailto:support@pixxel.space'
              external
              data-test-id='official-documentation-link'
            >
              support@pixxel.space
            </Link>{' '}
            to order images for L1C Level data.
          </Text>

          <Text size={2}>
            For more details, refer to the{' '}
            <Link
              variant='accent'
              href='https://support.pixxel.space/hc/en-us/articles/18372887952668-Firefly-Constellation-470-900-nm-with-5m-Resolution'
              external
              data-test-id='official-documentation-link'
            >
              product documentation
            </Link>
            .
          </Text>
        </Flex>
      </>
    ),
    show: true
  }
];
